/**
 * Shared StatusBadge component for displaying upload status
 * Provides consistent status display across upload pages
 */

import { Badge } from '@mantine/core';

export interface StatusBadgeProps {
  status: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'filled' | 'light' | 'outline' | 'dot';
  color?: string;
  children?: React.ReactNode;
}

/**
 * Status badge configuration mapping
 * Defines colors and labels for different upload statuses
 */
const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  // Processing statuses
  'processing': { color: 'green', label: 'Processing' },
  'optimizing': { color: 'cyan', label: 'Optimizing' },
  'saving': { color: 'teal', label: 'Saving' },
  'analyzing': { color: 'indigo', label: 'Analyzing' },
  'creating items': { color: 'violet', label: 'Creating Items' },
  'generating descriptions': { color: 'yellow', label: 'Generating Descriptions' },
  'generating translations': { color: 'orange', label: 'Generating Translations' },
  'generating AI properties': { color: 'indigo', label: 'Generating AI Properties' },
  'processing categories': { color: 'cyan', label: 'Processing Categories' },
  'saving menu items': { color: 'violet', label: 'Saving Items' },
  'processing categories with menu context': { color: 'cyan', label: 'Processing Categories' },
  'generating AI details only': { color: 'yellow', label: 'Generating AI Details' },
  
  // File-specific statuses
  'extracting menu items': { color: 'green', label: 'Extracting Items' },
  'optimizing image': { color: 'cyan', label: 'Optimizing Image' },
  'parsing csv': { color: 'yellow', label: 'Parsing CSV' },
  'csv_parsed': { color: 'green', label: 'CSV Parsed' },
  
  // Completion statuses
  'complete': { color: 'green', label: 'Complete' },
  'file_complete': { color: 'green', label: 'File Complete' },
  
  // Error statuses
  'error': { color: 'red', label: 'Error' },
  'failed': { color: 'red', label: 'Failed' }
};

/**
 * StatusBadge component for displaying upload status with appropriate colors
 * @param status - The status string to display
 * @param size - Badge size
 * @param variant - Badge variant
 * @param color - Override color
 * @param children - Custom content
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  size = 'sm', 
  variant = 'light',
  color,
  children
}) => {
  const config = STATUS_CONFIG[status] || { color: 'gray', label: status };
  const badgeColor = color || config.color;
  const badgeContent = children || config.label;
  
  return (
    <Badge 
      color={badgeColor} 
      size={size} 
      variant={variant}
    >
      {badgeContent}
    </Badge>
  );
};

/**
 * Hook for getting status badge configuration
 * @param status - The status string
 * @returns Status configuration object
 */
export const useStatusConfig = (status: string) => {
  return STATUS_CONFIG[status] || { color: 'gray', label: status };
};

export default StatusBadge;
